import { ISearchType } from '@search/offer-search-routes/src';
import { GeoKind } from '@search/filter-enums/enums/GeoKind';
import { DealType } from '@search/filter-enums/enums/DealType';
import { RealtyObjectType } from '@search/filter-enums/enums/RealtyObjectType';
import { Rooms } from '@search/filter-enums/enums/Rooms';
import { sortLabelMapping } from '@search/filter-enums/mappings/sort_label_mapping';
import { NewBuilding } from '@search/filter-enums/enums/NewBuilding';

import type { TipItem } from '../../../../view/tips/OffersSearchTips';

const geoKindDict: Record<GeoKind, string> = {
    [GeoKind.UNKNOWN]: '',
    [GeoKind.DISTRICT]: 'Район',
    [GeoKind.METRO]: 'Метро',
    [GeoKind.LOCALITY]: 'Населённый пункт',
    [GeoKind.AIRPORT]: 'Аэропорт',
    [GeoKind.APARTMENT]: '???',
    [GeoKind.AREA]: 'Район области',
    [GeoKind.COMPOSITE_OBJECT]: '???',
    [GeoKind.COUNTRY]: 'Страна',
    [GeoKind.ENTRANCE]: 'Подъезд',
    [GeoKind.HOUSE]: 'Дом',
    [GeoKind.HYDRO]: 'Река/озеро/водохранилище',
    [GeoKind.OTHER]: 'Прочее geo',
    [GeoKind.PROVINCE]: 'Область',
    [GeoKind.RAILWAY_STATION]: 'Ж/д странция',
    [GeoKind.ROUTE]: 'Линия метро/шоссе/ж.д. линия',
    [GeoKind.STATION]: 'Станции (не ж/д)',
    [GeoKind.STREET]: 'Улица',
    [GeoKind.STREET_ADDRESS]: '???',
    [GeoKind.VEGETATION]: 'Лес/парк/сад'
};

const roomDict = {
    [Rooms.UNKNOWN]: '',
    [Rooms.STUDIO]: 'студии',
    [Rooms.ROOM_1]: '1-комнатные',
    [Rooms.ROOM_2]: '2-комнатные',
    [Rooms.ROOM_3]: '3-комнатные',
    [Rooms.ROOM_4]: '4-комнатные',
    [Rooms.ROOM_5_AND_MORE]: '5 и более комнат',
    [Rooms.OPEN_PLAN]: 'со свободной планировкой'
};

const newBuildingDict = {
    [NewBuilding.NEW]: 'В новостройке',
    [NewBuilding.SECOND]: 'Во вторичке',
    [NewBuilding.UNKNOWN]: 'Во вторичке или новостройке'
};

const dealTypeDict = {
    [DealType.UNKNOWN]: '',
    [DealType.SELL]: 'покупка',
    [DealType.RENT]: 'аренда'
};

const categoryTypeDict = {
    [RealtyObjectType.UNKNOWN]: '',
    [RealtyObjectType.FLAT]: 'квартира',
    [RealtyObjectType.ROOM]: 'комната',
    [RealtyObjectType.LAND]: 'участок',
    [RealtyObjectType.HOUSE]: 'дом',
    [RealtyObjectType.COUNTRY_ESTATE]: 'усадьба'
};

const houseCategoryDict: Record<string, string> = {
    DUPLEX: 'дуплекс',
    PART_OF_HOUSE: 'часть дома',
    STANDALONE: 'отдельный дом',
    TOWNHOUSE: 'таунхаус'
};

const landStatusDict: Record<string, string> = {
    AGRICULTURAL: 'ЛПХ',
    DNP: 'ДНП',
    IZHS: 'ИЖС',
    SNT: 'СНТ'
};

export function getAnalyticsFiltersString({ searchParams, tipsAnalytics, region }: {
    region: string;
    tipsAnalytics: TipItem[];
    searchParams: ISearchType;
}) {
    // ключи не нужны, т.к. в title уже зашито человекопонятное название категории (просьба аналитика)
    const removeKeyFromCategoriesList = [ 'walls', 'totalArea', 'livingArea', 'kitchenArea', 'floorsTotal',
        'cellHeight', 'builtYear', 'floor', 'landArea', 'supplyOptions', 'houseArea' ];
    const removeCategories = [ 'district-group', 'mapBounds' ];

    // get additional filters
    const groupedValues = tipsAnalytics.reduce((acc, curr) => {
        const key = curr.kind ?? curr.id;

        // @ts-ignore
        if (! curr.title || removeCategories.includes(key)) return acc;

        // @ts-ignore
        let modifiedKey = removeKeyFromCategoriesList.includes(key) ? '' : key;

        if (typeof modifiedKey === 'number') modifiedKey = geoKindDict[modifiedKey];

        acc.set(
            modifiedKey,
            [
                ...(acc.has(modifiedKey) ? acc.get(modifiedKey) ?? [] : []),
                curr.title
            ]
        );

        return acc;
    }, new Map<TipItem['kind'] | TipItem['id'], string[]>());

    // added main filters
    groupedValues.set('region', [ region ]);
    if (searchParams.categoryType) groupedValues.set('categoryType', [ categoryTypeDict[searchParams.categoryType] ]);
    if (searchParams.dealType) groupedValues.set('dealType', [ dealTypeDict[searchParams.dealType] ]);
    if (searchParams.newBuilding.length > 0) {
        groupedValues.set(
            'Вторичка/новостройка',
            searchParams.newBuilding.map(newBuildingType => newBuildingDict[newBuildingType])
        );
    }
    if (searchParams.rooms.length > 0) {
        groupedValues.set('rooms', searchParams.rooms.map(roomType => roomDict[roomType]));
    }
    if (searchParams.landStatus.length > 0) {
        groupedValues.set('landStatus', searchParams.landStatus.map(status => landStatusDict[status]));
    }
    if (searchParams.houseCategory.length > 0) {
        groupedValues.set('houseCategory', searchParams.houseCategory.map(category => houseCategoryDict[category]));
    }
    if (searchParams.sort && searchParams.sort !== 'unknown') {
        // @ts-ignore
        groupedValues.set('Сортировка', [ sortLabelMapping[searchParams.sort] ]);
    }

    let tipsString = '';

    groupedValues.forEach((value, key) => {
        const values = value.join(key ? ', ' : '; ').concat('; ');

        if (key) {
            tipsString = tipsString.concat([ key, values ].join(': '));
            return;
        }
        tipsString = tipsString.concat(values);
    });

    return tipsString;
}
